import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { END_POINTS } from "../../Helper/Constant";
import { getApiRequest } from "../../services/getApiRequest";

const ACTIVE_ROOMS_LIMIT = 10;
const filterScoreSubmittedRooms = (rooms = []) =>
  rooms.filter((room) => room?.isScoreSubmitted !== true);
export const fetchActiveRooms = createAsyncThunk(
  "activeRooms/fetchActiveRooms",
  async (
    {
      gameTypeId,
      gameTypeIds,
      userId,
      page = 1,
      limit = ACTIVE_ROOMS_LIMIT,
      append = false,
    },
    { rejectWithValue }
  ) => {
    if (!userId) {
      return rejectWithValue("Missing userId");
    }

    try {
      const params = new URLSearchParams({
        userId,
        page: String(page),
        limit: String(limit),
      });

      const resolvedGameTypeIds = (() => {
        if (Array.isArray(gameTypeIds) && gameTypeIds.length > 0) {
          return gameTypeIds.filter(Boolean);
        }
        if (gameTypeId) {
          return Array.isArray(gameTypeId) ? gameTypeId.filter(Boolean) : [gameTypeId];
        }
        return [];
      })();

      resolvedGameTypeIds.forEach((id) => {
        params.append("gameTypeIds", id);
      });

      const response = await getApiRequest(
        `${END_POINTS.GET_ALL_ROOMS_BY_GAME}?${params.toString()}`
      );

      if (!response?.success) {
        return rejectWithValue(response?.message || "Failed to fetch active rooms");
      }

      const rooms = Array.isArray(response?.rooms) ? response.rooms : [];
      const pagination = response?.pagination || {};
      const resolvedPage =
        Number(pagination?.currentPage || response?.currentPage || page) || page;
      const resolvedLimit =
        Number(pagination?.limit || response?.limit || limit) || limit;
      const totalPages =
        Number(pagination?.totalPages || response?.totalPages) || 1;
      const total =
        Number(pagination?.total || response?.totalCount || response?.total) ||
        rooms.length;
      const hasMore = resolvedPage < totalPages;
      const isAvailableForRandomPlay = Boolean(response?.isAvailableForRandomPlay);
      const isAlreadyInGame = Boolean(response?.isAlreadyInGame);

      return {
        rooms,
        page: resolvedPage,
        limit: resolvedLimit,
        total,
        totalPages,
        hasMore,
        isAvailableForRandomPlay,
        isAlreadyInGame,
        append,
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.error?.message ||
        error?.message ||
        "Something went wrong while fetching active rooms"
      );
    }
  }
);

export const fetchIsInActiveMatch = createAsyncThunk(
  "activeRooms/fetchIsInActiveMatch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getApiRequest(END_POINTS.GET_IS_IN_ACTIVE_MATCH);
      if (!response?.success) {
        return rejectWithValue(
          response?.message || "Failed to fetch active match status"
        );
      }

      return Boolean(response?.isAlreadyInGame);
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.error?.message ||
        error?.message ||
        "Something went wrong while fetching active match status"
      );
    }
  }
);

const initialState = {
  rooms: [],
  page: 1,
  limit: ACTIVE_ROOMS_LIMIT,
  total: 0,
  totalPages: 1,
  hasMore: false,
  isAvailableForRandomPlay: false,
  isAlreadyInGame: false,
  currentGameTypeId: null,
  hasFetched: false,
  isLoading: false,
  error: null,
};

const activeRoomsSlice = createSlice({
  name: "activeRooms",
  initialState,
  reducers: {
    clearActiveRooms: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveRooms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActiveRooms.fulfilled, (state, action) => {
        const {
          rooms,
          append,
          page,
          limit,
          total,
          totalPages,
          hasMore,
          isAvailableForRandomPlay,
          isAlreadyInGame,
        } =
          action.payload;
        const requestedGameTypeIds =
          action.meta.arg?.gameTypeIds?.length > 0
            ? action.meta.arg.gameTypeIds
            : action.meta.arg?.gameTypeId
              ? [action.meta.arg.gameTypeId]
              : [];

        state.isLoading = false;
        state.rooms = append ? [...state.rooms, ...rooms] : rooms;
        state.page = page;
        state.limit = limit;
        state.total = total;
        state.totalPages = totalPages;
        state.hasMore = hasMore;
        state.isAvailableForRandomPlay = isAvailableForRandomPlay;
        state.isAlreadyInGame = isAlreadyInGame;
        state.currentGameTypeId =
          requestedGameTypeIds.length === 1 ? requestedGameTypeIds[0] : null;
        state.hasFetched = true;
      })
      .addCase(fetchActiveRooms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch active rooms";
      })
      .addCase(fetchIsInActiveMatch.fulfilled, (state, action) => {
        state.isAlreadyInGame = Boolean(action.payload);
      });
  },
});

export const { clearActiveRooms } = activeRoomsSlice.actions;

export const selectActiveRooms = createSelector(
  (state) => state.activeRooms?.rooms || [],
  (rooms) => filterScoreSubmittedRooms(rooms)
);
export const selectActiveRoomsLoading = (state) =>
  state.activeRooms?.isLoading || false;
export const selectActiveRoomsError = (state) => state.activeRooms?.error || null;
export const selectIsAvailableForRandomPlay = (state) =>
  state.activeRooms?.isAvailableForRandomPlay || false;
export const selectIsAlreadyInGame = (state) =>
  state.activeRooms?.isAlreadyInGame || false;
export const selectActiveRoomsGameTypeId = (state) =>
  state.activeRooms?.currentGameTypeId || null;
export const selectActiveRoomsHasFetched = (state) =>
  state.activeRooms?.hasFetched || false;
export const selectActiveRoomsPagination = createSelector(
  (state) => state.activeRooms,
  (activeRooms) => ({
    page: activeRooms?.page || 1,
    limit: activeRooms?.limit || ACTIVE_ROOMS_LIMIT,
    total: activeRooms?.total || 0,
    totalPages: activeRooms?.totalPages || 1,
    hasMore: activeRooms?.hasMore || false,
  })
);

export default activeRoomsSlice.reducer;
